import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import type { NewLegalEntityFormType } from "@/components/units/LegalEntityCreateForm/types";
import type { PaginationParams } from "@/types/api";

import { legalEntityApi } from "./legalEntityApi";
import type {
  AssignableResponse,
  IDetailsLegalEntity,
  LegalSubPosition,
  LinkedLegalEntityPayload,
} from "./types";

export const useGetAllLegalEntity = (params?: PaginationParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.legalEntity, params],
    queryFn: () => legalEntityApi.getLegalEntity(params),
  });
};

export const useCreateLegalEntityMutation = () => {
  return useMutation({
    mutationFn: (data: NewLegalEntityFormType) => {
      return legalEntityApi.postNewLegalEntity(data);
    },
  });
};

export const useGetLegalEntityIdQuery = (id: string) => {
  return useQuery<IDetailsLegalEntity>({
    queryKey: [QUERY_KEYS.legalEntity, id],
    queryFn: () => legalEntityApi.getLegalEntityById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });
};

export const useUpdateLegalEntityMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: NewLegalEntityFormType;
    }) => {
      return legalEntityApi.patchLegalEntity(id, data);
    },
  });
};

export const useLinkChildLegalEntityMutation = () => {
  return useMutation({
    mutationFn: (data: LinkedLegalEntityPayload) => {
      return legalEntityApi.postLinkChildLegalEntity(data);
    },
  });
};

export const useUnlinkChildLegalEntityMutation = () => {
  return useMutation({
    mutationFn: (data: LinkedLegalEntityPayload) => {
      return legalEntityApi.postUnlinkChildLegalEntity(data);
    },
  });
};

export const useDeactivateLegalEntityMutation = () => {
  return useMutation({
    mutationFn: (legalId: string) => {
      return legalEntityApi.deactivateLegalEntity(legalId);
    },
  });
};

export const useGetLegalEntitiesAvailableAsChildrenQuery = (id: string) => {
  return useQuery<LegalSubPosition[]>({
    queryKey: [QUERY_KEYS.legalEntity, id, "available-as-children"],
    queryFn: () => legalEntityApi.getLegalEntitiesAvailableAsChildren(id),
    enabled: !!id,
  });
};

export const useGetAssignableBrandsAndBranchesQuery = (groupId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.legalEntity, "assignable", groupId],
    queryFn: () => legalEntityApi.getAssignableBrandsAndBranches(groupId),
    enabled: !!groupId,
  });
};

export const useGetAssignableQuery = (id: string) => {
  return useQuery<AssignableResponse>({
    queryKey: [QUERY_KEYS.legalEntity, id, "assignable"],
    queryFn: () => legalEntityApi.getAssignable(id),
    enabled: !!id,
  });
};
